import type { ComponentType, SVGProps } from 'react'

import { ProductIcons } from '@/lib/misc/ProductIcons'

import ArrowsRightLeftIcon from '@icons/arrows-right-left'
import BellIcon from '@icons/bell'
import BuildingOfficeIcon from '@icons/building-office'
import ChartBarIcon from '@icons/chart-bar'
import CodeBracketIcon from '@icons/code-bracket'
import CreditCardIcon from '@icons/credit-card'
import DocumentTextIcon from '@icons/document-text'
import FunnelIcon from '@icons/funnel'
import Square3Stack3dIcon from '@icons/square-3-stack-3d'
import TagIcon from '@icons/tag'
import UsersIcon from '@icons/users'

export type KnowledgeBaseIcon = ComponentType<SVGProps<SVGSVGElement>>

export const KNOWLEDGE_BASE_BASE_URL: string =
  import.meta.env.VITE_KB_BASE_URL || '/kb'

export const KNOWLEDGE_BASE_PUBLIC_SITE_URL = 'https://docs.inter.link'

export const KNOWLEDGE_BASE_SOURCES = {
  public: { label: 'Public', tag: null },
  portal: { label: 'Portal', tag: 'Portal only' },
} as const

export type KnowledgeBaseSource = keyof typeof KNOWLEDGE_BASE_SOURCES

export const DEFAULT_KNOWLEDGE_BASE_SOURCE: KnowledgeBaseSource = 'public'

export interface KnowledgeBaseCatalogueArticle {
  path: string
  title: string
  description: string
}

export interface KnowledgeBaseCatalogueSection {
  title: string
  articles: KnowledgeBaseCatalogueArticle[]
}

export interface KnowledgeBaseCatalogue {
  sections: KnowledgeBaseCatalogueSection[]
}

export const KNOWLEDGE_BASE_CATEGORY_TONES = [
  'bg-accent-10 text-accent-100',
  'bg-status-good-50 text-status-good-700',
  'bg-status-info-50 text-status-info-700',
  'bg-auxiliary-3 text-brand-3',
  'bg-status-warning-50 text-status-warning-900',
] as const

export interface KnowledgeBaseCategory {
  label: string
  tone: string
}

export interface KnowledgeBaseDocument {
  path: string
  title: string
  description: string
  category: KnowledgeBaseCategory
  icon: KnowledgeBaseIcon
}

export const KNOWLEDGE_BASE_DEFAULT_ICON: KnowledgeBaseIcon = DocumentTextIcon

export const KNOWLEDGE_BASE_ICONS: Record<string, KnowledgeBaseIcon> = {
  'using-portal': UsersIcon,
  api: CodeBracketIcon,
  pricing: CreditCardIcon,
  ports: ProductIcons.port_icon,
  'ip-transit': ProductIcons.ip_transit,
  'ip-access': ProductIcons.ip_access,
  'ddos-protection': ProductIcons.ddos_protection,
  'ddos-protection-templates': Square3Stack3dIcon,
  flowspec: FunnelIcon,
  'flex-ethernet': ProductIcons.flex_ethernet,
  flextunnel: ProductIcons.cloud_swap,
  'cloud-connect': ProductIcons.cloud_connect,
  'office-fiber': BuildingOfficeIcon,
  insights: ChartBarIcon,
  'bgp-communities': TagIcon,
  'routing-policies': ArrowsRightLeftIcon,
  'peering-policy': UsersIcon,
  'release-notes': BellIcon,
}

export interface KnowledgeBaseGuide {
  title: string
  steps: string[]
}

export const KNOWLEDGE_BASE_GUIDES: KnowledgeBaseGuide[] = [
  {
    title: 'Getting started',
    steps: [
      'public/using-portal',
      'public/ports',
      'public/pricing',
      'public/api',
    ],
  },
]
